import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { FilterGroupOption } from "@components/common/data-grid/adm-data-grid-types";
import { allAvailableBundles } from "@utils/product/bundle-products";
import { allAvailableCustomProducts } from "@utils/product/custom-products";
import {
  AllHoloBuilderLegacyPlans,
  AllSphereXg2026Products,
  AllSphereXgProducts,
  AllTrialProducts,
} from "@utils/product/product-types";
import { BaseProduct } from "@utils/product/product-utils-types";
import { allAvailableTrialProducts } from "@utils/product/trial-products";

/** A subscription the features can be compared against, offered as one column of the table */
export type Subscription = {
  /** Id of the product, used both as the filter option id and as the field of its column */
  id: string;

  /** The name the plan is sold under */
  name: string;

  /** What the plan grants, shown as a tooltip on its column header */
  description?: string;

  /** The kind of plan, shown below the name to tell the similar plan names apart */
  planType: string;

  /** Heading the plan is offered under in the Subscriptions filter */
  groupLabel: string;
};

/**
 * Every product the Subscriptions filter can offer, keyed by its id. The legacy plans are modelled
 * as bundles, which is why the bundles are part of the lookup as well.
 */
export const subscriptionProductsById: Record<string, BaseProduct> = {
  ...allAvailableCustomProducts,
  ...allAvailableTrialProducts,
  ...allAvailableBundles,
};

/**
 * The plan sections of the Add Subscription page, in the same order, so that a plan is found here
 * under the same heading it is created under. That heading is repeated on every plan, since the
 * plan names alone are too similar to tell the years apart.
 */
const subscriptionSections: {
  /** The heading of the section, worded as on the Add Subscription page */
  groupLabel: string;

  /** The kind of plan the section holds */
  planType: string;

  /** Ids of the products of this section, in the order they are offered in */
  productIds: readonly string[];

  /** Whether the section is shown when the page is opened for the first time */
  isDefaultSelected: boolean;
}[] = [
  {
    groupLabel: "Sphere XG Plans 2026",
    planType: "2026 Plan",
    productIds: AllSphereXg2026Products,
    isDefaultSelected: true,
  },
  {
    groupLabel: "Sphere XG Plans 2025",
    planType: "2025 Plan",
    productIds: AllSphereXgProducts,
    isDefaultSelected: true,
  },
  {
    groupLabel: "Sphere XG Trial Plans",
    planType: "Trial Plan",
    productIds: AllTrialProducts,
    isDefaultSelected: true,
  },
  {
    groupLabel: "HoloBuilder Legacy Plans",
    planType: "HoloBuilder Legacy Plan",
    productIds: AllHoloBuilderLegacyPlans,
    isDefaultSelected: false,
  },
];

/** The subscriptions shown on a first visit: every plan except the legacy ones */
export const defaultSelectedSubscriptionIds: string[] = subscriptionSections
  .filter((section) => section.isDefaultSelected)
  .flatMap((section) => section.productIds);

/**
 * @param allBundles the bundles returned by the backend, holding the name and description of the
 * plans the frontend registry does not describe itself, such as the legacy plans
 * @returns every subscription of the Add Subscription page, in the same order
 */
export function getSubscriptions(
  allBundles: FaroCoreApiTypes.AdmBundle[]
): Subscription[] {
  return subscriptionSections.flatMap(({ groupLabel, planType, productIds }) =>
    productIds.map((productId) => {
      const product = subscriptionProductsById[productId];
      const bundleIdentifier =
        product.identifiers.length === 1 ? product.identifiers[0] : productId;
      const bundle = allBundles.find(
        ({ identifier }) => identifier === bundleIdentifier
      );

      return {
        id: productId,
        name: product.name ?? bundle?.name ?? productId,
        description: product.description ?? bundle?.description,
        planType,
        groupLabel,
      };
    })
  );
}

/** @returns an option for every subscription, headed by the section it is created under */
export function getSubscriptionFilterOptions(
  subscriptions: Subscription[]
): FilterGroupOption[] {
  return subscriptions.map(({ id, name, groupLabel }) => ({
    id,
    label: name,
    groupLabel,
  }));
}

/**
 * @returns an option for every bundle the backend knows, told apart by its identifier and sorted by
 * name, since the backend returns them in no particular order
 */
export function getBundleFilterOptions(
  allBundles: FaroCoreApiTypes.AdmBundle[]
): FilterGroupOption[] {
  return allBundles
    .map((bundle) => ({
      id: bundle.identifier,
      label: bundle.name,
      subtitle: bundle.identifier,
    }))
    .sort((first, second) => first.label.localeCompare(second.label));
}
